import { useState, useEffect } from 'react'
import { useReports } from '../hooks/useApi'
import { formatCurrency, getYearStart, getYearEnd, getStartOfMonth, getEndOfMonth, getQuarterStart, getQuarterEnd } from '../lib/utils'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import { toast } from 'sonner'
import { FileText, Download, Loader2, TrendingUp, TrendingDown, Calendar, FileDown } from 'lucide-react'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { analytics } from '../lib/analytics'

export default function Reports() {
  const [activeTab, setActiveTab] = useState('pnl')
  const [pnlData, setPnlData] = useState(null)
  const [perDiemData, setPerDiemData] = useState(null)
  const [loadingPnl, setLoadingPnl] = useState(false)
  const [loadingPerDiem, setLoadingPerDiem] = useState(false)
  
  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth()
  const currentQuarter = Math.floor(currentMonth / 3) + 1
  
  const [pnlStartDate, setPnlStartDate] = useState(getStartOfMonth())
  const [pnlEndDate, setPnlEndDate] = useState(getEndOfMonth())
  const [perDiemYear, setPerDiemYear] = useState(currentYear.toString())
  
  // Date range selection state
  const [dateRangeType, setDateRangeType] = useState('month')
  const [selectedMonth, setSelectedMonth] = useState((currentMonth + 1).toString())
  const [selectedQuarter, setSelectedQuarter] = useState(currentQuarter.toString())
  const [selectedYear, setSelectedYear] = useState(currentYear.toString())
  
  const { fetchPnLReport, fetchPerDiemReport } = useReports()

  // Update date range when selection changes
  const updateDateRange = (type, month, quarter, year) => {
    const yearNum = parseInt(year)
    
    if (type === 'month') {
      const monthNum = parseInt(month)
      const startDate = new Date(yearNum, monthNum - 1, 1)
      const endDate = new Date(yearNum, monthNum, 0)
      setPnlStartDate(`${yearNum}-${String(monthNum).padStart(2, '0')}-01`)
      setPnlEndDate(`${yearNum}-${String(monthNum).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`)
    } else if (type === 'quarter') {
      const quarterNum = parseInt(quarter)
      const startMonth = (quarterNum - 1) * 3 + 1
      const endMonth = quarterNum * 3
      const lastDay = new Date(yearNum, endMonth, 0).getDate()
      setPnlStartDate(`${yearNum}-${String(startMonth).padStart(2, '0')}-01`)
      setPnlEndDate(`${yearNum}-${String(endMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`)
    } else if (type === 'year') {
      setPnlStartDate(`${yearNum}-01-01`)
      setPnlEndDate(`${yearNum}-12-31`)
    }
    // 'custom' type doesn't auto-update - user sets dates manually
  }

  const handleDateRangeTypeChange = (type) => {
    setDateRangeType(type)
    if (type !== 'custom') {
      updateDateRange(type, selectedMonth, selectedQuarter, selectedYear)
    }
  }

  const handleMonthChange = (month) => {
    setSelectedMonth(month)
    updateDateRange('month', month, selectedQuarter, selectedYear)
  }

  const handleQuarterChange = (quarter) => {
    setSelectedQuarter(quarter)
    updateDateRange('quarter', selectedMonth, quarter, selectedYear)
  }

  const handleYearChange = (year) => {
    setSelectedYear(year)
    updateDateRange(dateRangeType, selectedMonth, selectedQuarter, year)
  }

  const months = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ]

  const quarters = [
    { value: '1', label: 'Q1 (Jan - Mar)' },
    { value: '2', label: 'Q2 (Apr - Jun)' },
    { value: '3', label: 'Q3 (Jul - Sep)' },
    { value: '4', label: 'Q4 (Oct - Dec)' },
  ]

  const years = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3]

  const handleGeneratePnL = async () => {
    setLoadingPnl(true)
    try {
      const data = await fetchPnLReport(pnlStartDate, pnlEndDate)
      setPnlData(data)
      analytics.plReportGenerated('view', dateRangeType)
      toast.success('Report generated')
    } catch (err) {
      toast.error('Failed to generate report')
    } finally {
      setLoadingPnl(false)
    }
  }

  const handleGeneratePerDiem = async () => {
    setLoadingPerDiem(true)
    try {
      const data = await fetchPerDiemReport(parseInt(perDiemYear))
      setPerDiemData(data)
      analytics.perDiemReportGenerated('view')
      toast.success('Report generated')
    } catch (err) {
      toast.error('Failed to generate report')
    } finally {
      setLoadingPerDiem(false)
    }
  }

  const exportToCSV = (data, filename) => {
    let csvContent = ''
    
    if (filename.includes('pnl')) {
      csvContent = 'Profit & Loss Report\n'
      csvContent += `Period,${data.period.start} to ${data.period.end}\n\n`
      csvContent += 'REVENUE\n'
      csvContent += 'Date,Route,Broker,Category,Reimbursement,Amount\n'
      data.revenue.loads.forEach(l => {
        csvContent += `${l.date},"${l.pickup} → ${l.delivery}","${l.broker}","${l.category || 'linehaul'}",${l.is_reimbursement ? 'Yes' : 'No'},${l.rate}\n`
      })
      csvContent += `\nTotal Revenue,,,,,${data.revenue.total}\n`
      csvContent += `Reimbursements,,,,,-${data.revenue.reimbursements || 0}\n`
      csvContent += `Gross Revenue,,,,,${data.revenue.gross_revenue || data.revenue.total}\n\n`
      csvContent += 'EXPENSES BY CATEGORY\n'
      csvContent += 'Category,Amount\n'
      Object.entries(data.expenses.by_category).forEach(([cat, info]) => {
        csvContent += `"${cat}",${info.total}\n`
      })
      csvContent += `\nTotal Expenses,,${data.expenses.total}\n`
      csvContent += `Net Profit,,${data.net_profit}\n`
    } else {
      csvContent = 'Per Diem Report\n'
      csvContent += `Year,${data.year}\n\n`
      csvContent += `Partial Days,${data.partial_days_count},@ ${data.partial_rate},${data.partial_total}\n`
      csvContent += `Full Days,${data.full_days_count},@ ${data.full_rate},${data.full_total}\n`
      csvContent += `\nTotal Per Diem,,,${data.total_per_diem}\n`
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${filename}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success('CSV downloaded')
  }

  const exportPnLToPDF = (data) => {
    analytics.plReportGenerated('pdf', dateRangeType)
    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    
    // Colors matching app theme (for headers only)
    const blueColor = [59, 130, 246]    // Revenue - blue
    const yellowColor = [234, 179, 8]    // Expenses - yellow
    const greenColor = [34, 197, 94]     // Profit - green
    
    // Title
    doc.setFontSize(16)
    doc.setFont('helvetica', 'bold')
    doc.text('PROFIT & LOSS STATEMENT', pageWidth / 2, 18, { align: 'center' })
    
    // Period
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.text(`Period: ${data.period.start} to ${data.period.end}`, pageWidth / 2, 26, { align: 'center' })
    
    // Summary section
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.text('SUMMARY', 14, 38)
    
    autoTable(doc, {
      startY: 42,
      head: [['Description', 'Amount']],
      body: [
        ['Total Revenue', formatCurrency(data.revenue.total)],
        ['  Reimbursements', `- ${formatCurrency(data.revenue.reimbursements || 0)}`],
        ['Gross Revenue', formatCurrency(data.revenue.gross_revenue || data.revenue.total)],
        ['Total Expenses', formatCurrency(data.expenses.total)],
        ['Net Profit', formatCurrency(data.net_profit)],
        ['Profit Margin', `${data.profit_margin.toFixed(1)}%`],
      ],
      theme: 'striped',
      headStyles: { fillColor: greenColor, textColor: [255, 255, 255] },
      styles: { fontSize: 9, textColor: [0, 0, 0], font: 'helvetica' },
      columnStyles: { 
        0: { halign: 'left' },
        1: { halign: 'right' } 
      },
      didParseCell: function(hookData) {
        // Right-align Amount header
        if (hookData.section === 'head' && hookData.column.index === 1) {
          hookData.cell.styles.halign = 'right'
        }
        // Italic the Reimbursements row
        if (hookData.section === 'body' && hookData.row.index === 1) {
          hookData.cell.styles.fontStyle = 'italic'
          hookData.cell.styles.textColor = [180, 130, 30]
        }
        // Bold the Gross Revenue row
        if (hookData.section === 'body' && hookData.row.index === 2) {
          hookData.cell.styles.fontStyle = 'bold'
        }
      }
    })
    
    // Revenue Details
    let yPos = doc.lastAutoTable.finalY + 12
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(0, 0, 0)
    doc.text('REVENUE DETAILS', 14, yPos)
    
    // Separate regular loads and reimbursements
    const regularLoads = data.revenue.loads.filter(l => !l.is_reimbursement)
    const reimbursementLoads = data.revenue.loads.filter(l => l.is_reimbursement)
    
    const loadRows = regularLoads.map(l => [
      l.date,
      l.pickup || 'N/A',
      l.delivery || 'N/A',
      l.broker || 'Direct',
      formatCurrency(l.rate)
    ])
    
    // Add subtotal for gross revenue
    const grossRevenue = data.revenue.gross_revenue || data.revenue.total
    loadRows.push(['Gross Revenue', '', '', '', formatCurrency(grossRevenue)])
    
    // Add reimbursements section if any
    if (reimbursementLoads.length > 0) {
      loadRows.push(['', '', '', '', ''])  // Spacer row
      loadRows.push(['REIMBURSEMENTS', '', '', '', ''])
      reimbursementLoads.forEach(l => {
        loadRows.push([l.date, l.pickup || 'N/A', l.delivery || 'N/A', l.broker || 'Direct', formatCurrency(l.rate)])
      })
      loadRows.push(['Total Reimbursements', '', '', '', formatCurrency(data.revenue.reimbursements || 0)])
    }
    
    // Add total revenue row
    loadRows.push(['TOTAL REVENUE', '', '', '', formatCurrency(data.revenue.total)])
    
    autoTable(doc, {
      startY: yPos + 4,
      head: [['Date', 'Pickup', 'Delivery', 'Broker', 'Amount']],
      body: loadRows,
      theme: 'striped',
      headStyles: { fillColor: blueColor, textColor: [255, 255, 255] },
      styles: { fontSize: 9, textColor: [0, 0, 0], font: 'helvetica' },
      columnStyles: { 
        0: { halign: 'left' },
        1: { halign: 'left' },
        2: { halign: 'left' },
        3: { halign: 'left' },
        4: { halign: 'right' } 
      },
      didParseCell: function(hookData) {
        // Right-align Amount header
        if (hookData.section === 'head' && hookData.column.index === 4) {
          hookData.cell.styles.halign = 'right'
        }
        // Bold summary rows (Gross Revenue, Total Reimbursements, TOTAL REVENUE, REIMBURSEMENTS header)
        if (hookData.section === 'body') {
          const firstCell = hookData.row.raw[0]
          if (firstCell === 'Gross Revenue' || firstCell === 'Total Reimbursements' || 
              firstCell === 'TOTAL REVENUE' || firstCell === 'REIMBURSEMENTS') {
            hookData.cell.styles.fontStyle = 'bold'
          }
        }
      }
    })
    
    // Expenses by Category
    yPos = doc.lastAutoTable.finalY + 12
    
    // Check if we need a new page
    if (yPos > 220) {
      doc.addPage()
      yPos = 18
    }
    
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(0, 0, 0)
    doc.text('EXPENSES BY CATEGORY', 14, yPos)
    
    const expenseRows = Object.entries(data.expenses.by_category).map(([cat, info]) => [
      cat,
      formatCurrency(info.total)
    ])
    
    // Add total row as regular body row with bold styling
    expenseRows.push(['Total Expenses', formatCurrency(data.expenses.total)])
    
    autoTable(doc, {
      startY: yPos + 4,
      head: [['Category', 'Amount']],
      body: expenseRows,
      theme: 'striped',
      headStyles: { fillColor: yellowColor, textColor: [255, 255, 255] },
      styles: { fontSize: 9, textColor: [0, 0, 0], font: 'helvetica' },
      columnStyles: { 
        0: { halign: 'left' },
        1: { halign: 'right' } 
      },
      didParseCell: function(hookData) {
        // Right-align Amount header
        if (hookData.section === 'head' && hookData.column.index === 1) {
          hookData.cell.styles.halign = 'right'
        }
        // Bold the Total Expenses row
        if (hookData.section === 'body' && hookData.row.index === expenseRows.length - 1) {
          hookData.cell.styles.fontStyle = 'bold'
        }
      }
    })
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages()
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i)
      doc.setFontSize(7)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(128, 128, 128)
      doc.text(
        `Generated by Driver PNL - ${new Date().toLocaleDateString()}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 8,
        { align: 'center' }
      )
    }
    
    doc.save(`pnl-${data.period.start}-${data.period.end}.pdf`)
    toast.success('PDF downloaded')
  }

  const exportPerDiemToPDF = (data) => {
    analytics.perDiemReportGenerated('pdf')
    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    
    // Purple color for per diem (matching app theme)
    const purpleColor = [147, 51, 234]
    
    // Title
    doc.setFontSize(16)
    doc.setFont('helvetica', 'bold')
    doc.text('PER DIEM REPORT', pageWidth / 2, 18, { align: 'center' })
    
    // Year
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.text(`Tax Year: ${data.year}`, pageWidth / 2, 26, { align: 'center' })
    
    // YTD Summary Table
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(0, 0, 0)
    doc.text('YEAR TO DATE SUMMARY', 14, 36)
    
    // Add total row to YTD summary
    const ytdRows = [
      ['Partial Days', data.partial_days_count.toString(), formatCurrency(data.partial_rate), formatCurrency(data.partial_total)],
      ['Full Days', data.full_days_count.toString(), formatCurrency(data.full_rate), formatCurrency(data.full_total)],
      ['Total Per Diem', (data.partial_days_count + data.full_days_count).toString(), '', formatCurrency(data.total_per_diem)]
    ]
    
    autoTable(doc, {
      startY: 40,
      head: [['Day Type', 'Count', 'Rate', 'Subtotal']],
      body: ytdRows,
      theme: 'striped',
      headStyles: { fillColor: purpleColor, textColor: [255, 255, 255] },
      styles: { fontSize: 8, textColor: [0, 0, 0], font: 'helvetica' },
      columnStyles: { 
        0: { halign: 'left' },
        1: { halign: 'center' },
        2: { halign: 'right' },
        3: { halign: 'right' }
      },
      didParseCell: function(hookData) {
        // Right-align Rate and Subtotal headers
        if (hookData.section === 'head') {
          if (hookData.column.index === 1) {
            hookData.cell.styles.halign = 'center'
          } else if (hookData.column.index === 2 || hookData.column.index === 3) {
            hookData.cell.styles.halign = 'right'
          }
        }
        // Bold the Total row
        if (hookData.section === 'body' && hookData.row.index === ytdRows.length - 1) {
          hookData.cell.styles.fontStyle = 'bold'
        }
      }
    })
    
    // Monthly Breakdown
    let yPos = doc.lastAutoTable.finalY + 12
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(0, 0, 0)
    doc.text('MONTHLY BREAKDOWN', 14, yPos)
    
    if (data.monthly_breakdown && data.monthly_breakdown.length > 0) {
      const monthlyRows = data.monthly_breakdown.map(m => [
        m.month,
        m.partial_count.toString(),
        m.full_count.toString(),
        (m.partial_count + m.full_count).toString(),
        formatCurrency(m.total)
      ])
      
      autoTable(doc, {
        startY: yPos + 4,
        head: [['Month', 'Partial', 'Full', 'Total Days', 'Amount']],
        body: monthlyRows,
        theme: 'striped',
        headStyles: { fillColor: purpleColor, textColor: [255, 255, 255] },
        styles: { fontSize: 8, textColor: [0, 0, 0], font: 'helvetica' },
        columnStyles: { 
          0: { halign: 'left' },
          1: { halign: 'center' },
          2: { halign: 'center' },
          3: { halign: 'center' },
          4: { halign: 'right' }
        },
        didParseCell: function(hookData) {
          // Align headers to match columns
          if (hookData.section === 'head') {
            if (hookData.column.index === 0) {
              hookData.cell.styles.halign = 'left'
            } else if (hookData.column.index >= 1 && hookData.column.index <= 3) {
              hookData.cell.styles.halign = 'center'
            } else if (hookData.column.index === 4) {
              hookData.cell.styles.halign = 'right'
            }
          }
        }
      })
    } else {
      doc.setFontSize(9)
      doc.setFont('helvetica', 'italic')
      doc.text('No per diem days recorded for this year.', 14, yPos + 8)
    }
    
    // Disclaimer
    yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 15 : yPos + 20
    doc.setFontSize(8)
    doc.setFont('helvetica', 'italic')
    doc.setTextColor(100)
    const disclaimer = 'Note: Per diem calculations are estimates based on IRS guidelines. Consult a tax professional for accurate tax advice.'
    doc.text(disclaimer, 14, yPos, { maxWidth: pageWidth - 28 })
    
    // Footer
    doc.setFontSize(7)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(128, 128, 128)
    doc.text(
      `Generated by Road Tracker - ${new Date().toLocaleDateString()}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 8,
      { align: 'center' }
    )
    
    doc.save(`per-diem-${data.year}.pdf`)
    toast.success('PDF downloaded')
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      <div className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight font-['Barlow_Condensed']">
          REPORTS
        </h1>
        <p className="text-muted-foreground">
          Generate P&L and Per Diem reports
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 h-12">
          <TabsTrigger value="pnl" className="h-10" data-testid="tab-pnl">
            <FileText className="w-4 h-4 mr-2" />
            P&L Report
          </TabsTrigger>
          <TabsTrigger value="perdiem" className="h-10" data-testid="tab-perdiem">
            <Calendar className="w-4 h-4 mr-2" />
            Per Diem
          </TabsTrigger>
        </TabsList>

        {/* P&L Report Tab */}
        <TabsContent value="pnl" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-['Barlow_Condensed']">
                SELECT DATE RANGE
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Period Type Selection */}
              <div className="space-y-2">
                <Label>Period Type</Label>
                <Select value={dateRangeType} onValueChange={handleDateRangeTypeChange}>
                  <SelectTrigger className="h-12" data-testid="period-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month">Month</SelectItem>
                    <SelectItem value="quarter">Quarter</SelectItem>
                    <SelectItem value="year">Year</SelectItem>
                    <SelectItem value="custom">Custom Dates</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Month Selection */}
              {dateRangeType === 'month' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Month</Label>
                    <Select value={selectedMonth} onValueChange={handleMonthChange}>
                      <SelectTrigger className="h-12" data-testid="month-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {months.map((m) => (
                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Year</Label>
                    <Select value={selectedYear} onValueChange={handleYearChange}>
                      <SelectTrigger className="h-12" data-testid="year-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {years.map((y) => (
                          <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Quarter Selection */}
              {dateRangeType === 'quarter' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Quarter</Label>
                    <Select value={selectedQuarter} onValueChange={handleQuarterChange}>
                      <SelectTrigger className="h-12" data-testid="quarter-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {quarters.map((q) => (
                          <SelectItem key={q.value} value={q.value}>{q.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Year</Label>
                    <Select value={selectedYear} onValueChange={handleYearChange}>
                      <SelectTrigger className="h-12" data-testid="year-select-quarter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {years.map((y) => (
                          <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Year Selection */}
              {dateRangeType === 'year' && (
                <div className="space-y-2">
                  <Label>Year</Label>
                  <Select value={selectedYear} onValueChange={handleYearChange}>
                    <SelectTrigger className="h-12" data-testid="year-select-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((y) => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Custom Date Selection */}
              {dateRangeType === 'custom' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={pnlStartDate}
                      onChange={(e) => setPnlStartDate(e.target.value)}
                      className="h-12"
                      data-testid="pnl-start-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={pnlEndDate}
                      onChange={(e) => setPnlEndDate(e.target.value)}
                      className="h-12"
                      data-testid="pnl-end-date"
                    />
                  </div>
                </div>
              )}

              {/* Show selected date range */}
              <div className="p-3 bg-secondary/50 rounded-lg text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Selected Range: </span>
                {pnlStartDate} to {pnlEndDate}
              </div>
              
              <Button
                onClick={handleGeneratePnL}
                disabled={loadingPnl}
                className="w-full h-12 rounded-full font-semibold"
                data-testid="generate-pnl-btn"
              >
                {loadingPnl ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
                Generate P&L Report
              </Button>
            </CardContent>
          </Card>

          {/* P&L Results */}
          {pnlData && (
            <Card data-testid="pnl-report-results">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-['Barlow_Condensed']">
                    PROFIT & LOSS STATEMENT
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => exportToCSV(pnlData, `pnl-${pnlStartDate}-${pnlEndDate}`)}
                      data-testid="export-pnl-csv"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      CSV
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => exportPnLToPDF(pnlData)}
                      data-testid="export-pnl-pdf"
                    >
                      <FileDown className="w-4 h-4 mr-2" />
                      PDF
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  {pnlData.period.start} to {pnlData.period.end}
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Summary KPIs */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-blue-500/10 rounded-xl">
                    <TrendingUp className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                    <p className="text-xs text-muted-foreground uppercase">Revenue</p>
                    <p className="text-xl font-bold text-blue-500 kpi-value">
                      {formatCurrency(pnlData.revenue.total, 0)}
                    </p>
                  </div>
                  <div className="text-center p-4 bg-yellow-500/10 rounded-xl">
                    <TrendingDown className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
                    <p className="text-xs text-muted-foreground uppercase">Expenses</p>
                    <p className="text-xl font-bold text-yellow-500 kpi-value">
                      {formatCurrency(pnlData.expenses.total, 0)}
                    </p>
                  </div>
                  <div className="text-center p-4 bg-green-500/10 rounded-xl">
                    <FileText className="w-6 h-6 text-green-500 mx-auto mb-2" />
                    <p className="text-xs text-muted-foreground uppercase">Net Profit</p>
                    <p className="text-xl font-bold text-green-500 kpi-value">
                      {formatCurrency(pnlData.net_profit, 0)}
                    </p>
                  </div>
                </div>

                {/* Revenue Breakdown */}
                <div className="p-4 bg-blue-500/10 rounded-xl" data-testid="revenue-breakdown">
                  <h3 className="font-semibold mb-3 font-['Barlow_Condensed'] uppercase tracking-wide text-blue-500 text-sm">
                    Revenue Breakdown
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Total Revenue</span>
                      <span className="font-semibold text-blue-500">
                        {formatCurrency(pnlData.revenue.total)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pl-3 border-l-2 border-amber-500/50">
                      <span className="text-sm italic text-amber-500">Reimbursements</span>
                      <span className="font-semibold italic text-amber-500">
                        {pnlData.revenue.reimbursements > 0
                          ? `- ${formatCurrency(pnlData.revenue.reimbursements)}`
                          : formatCurrency(0)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-border">
                      <span className="text-sm font-bold">Gross Revenue</span>
                      <span className="text-lg font-bold text-blue-500">
                        {formatCurrency(pnlData.revenue.gross_revenue || pnlData.revenue.total)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Fixed vs Variable Breakdown */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-purple-500/10 rounded-xl">
                    <p className="text-xs text-muted-foreground uppercase mb-1">Fixed Costs</p>
                    <p className="text-lg font-bold text-purple-500">
                      {formatCurrency(pnlData.expenses.fixed_total || 0, 0)}
                    </p>
                  </div>
                  <div className="p-4 bg-yellow-500/10 rounded-xl">
                    <p className="text-xs text-muted-foreground uppercase mb-1">Variable Costs</p>
                    <p className="text-lg font-bold text-yellow-500">
                      {formatCurrency(pnlData.expenses.variable_total || 0, 0)}
                    </p>
                  </div>
                </div>

                {/* Cost Per Mile */}
                {pnlData.total_miles > 0 && (
                  <div className="p-4 bg-secondary rounded-xl">
                    <div className="flex justify-between items-center">
                      <span>Variable Cost Per Mile</span>
                      <span className="font-bold text-lg text-yellow-500">
                        ${pnlData.cost_per_mile?.toFixed(2) || '0.00'}/mi
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Based on {pnlData.total_miles.toLocaleString()} miles tracked
                    </p>
                  </div>
                )}

                {/* Fixed Costs Breakdown */}
                {pnlData.expenses.fixed_by_category && Object.keys(pnlData.expenses.fixed_by_category).length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-3 font-['Barlow_Condensed'] uppercase tracking-wide text-purple-500">
                      Fixed Costs Breakdown
                    </h3>
                    <div className="space-y-2">
                      {Object.entries(pnlData.expenses.fixed_by_category).map(([category, info]) => (
                        <div key={category} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                          <span className="text-sm">{category}</span>
                          <span className="font-semibold text-purple-500">{formatCurrency(info.total)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Variable Costs Breakdown */}
                {pnlData.expenses.variable_by_category && Object.keys(pnlData.expenses.variable_by_category).length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-3 font-['Barlow_Condensed'] uppercase tracking-wide text-yellow-500">
                      Variable Costs Breakdown
                    </h3>
                    <div className="space-y-2">
                      {Object.entries(pnlData.expenses.variable_by_category).map(([category, info]) => (
                        <div key={category} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                          <span className="text-sm">{category}</span>
                          <span className="font-semibold text-yellow-500">{formatCurrency(info.total)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Profit Margin */}
                <div className="p-4 bg-green-500/10 rounded-xl">
                  <div className="flex justify-between items-center">
                    <span>Profit Margin</span>
                    <span className="font-bold text-lg text-green-500">
                      {pnlData.profit_margin.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Per Diem Report Tab */}
        <TabsContent value="perdiem" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-['Barlow_Condensed']">
                SELECT YEAR
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Select value={perDiemYear} onValueChange={setPerDiemYear}>
                <SelectTrigger className="h-12" data-testid="perdiem-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[currentYear, currentYear - 1, currentYear - 2].map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button
                onClick={handleGeneratePerDiem}
                disabled={loadingPerDiem}
                className="w-full h-12 rounded-full font-semibold"
                data-testid="generate-perdiem-btn"
              >
                {loadingPerDiem ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}
                Generate Per Diem Report
              </Button>
            </CardContent>
          </Card>

          {/* Per Diem Results */}
          {perDiemData && (
            <Card data-testid="perdiem-report-results">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-['Barlow_Condensed']">
                    PER DIEM REPORT - {perDiemData.year}
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => exportToCSV(perDiemData, `perdiem-${perDiemData.year}`)}
                      data-testid="export-perdiem-csv"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      CSV
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => exportPerDiemToPDF(perDiemData)}
                      data-testid="export-perdiem-pdf"
                    >
                      <FileDown className="w-4 h-4 mr-2" />
                      PDF
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* YTD Summary */}
                <div>
                  <h3 className="font-semibold mb-3 font-['Barlow_Condensed'] uppercase tracking-wide text-purple-500">
                    Year to Date Summary
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-3 border-b border-border">
                      <div>
                        <p className="font-medium">Partial Days</p>
                        <p className="text-sm text-muted-foreground">
                          {perDiemData.partial_days_count} days @ {formatCurrency(perDiemData.partial_rate, 0)}
                        </p>
                      </div>
                      <span className="font-semibold text-lg text-purple-500">
                        {formatCurrency(perDiemData.partial_total, 0)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center py-3 border-b border-border">
                      <div>
                        <p className="font-medium">Full Days</p>
                        <p className="text-sm text-muted-foreground">
                          {perDiemData.full_days_count} days @ {formatCurrency(perDiemData.full_rate, 0)}
                        </p>
                      </div>
                      <span className="font-semibold text-lg text-purple-500">
                        {formatCurrency(perDiemData.full_total, 0)}
                      </span>
                    </div>
                  </div>

                  <div className="p-4 bg-purple-500/10 rounded-xl mt-4">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">Total Per Diem Allowance</span>
                      <span className="text-2xl font-bold text-purple-500 kpi-value">
                        {formatCurrency(perDiemData.total_per_diem, 0)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Total days on road: {perDiemData.partial_days_count + perDiemData.full_days_count}
                    </p>
                  </div>
                </div>

                {/* Monthly Breakdown */}
                {perDiemData.monthly_breakdown && perDiemData.monthly_breakdown.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-3 font-['Barlow_Condensed'] uppercase tracking-wide text-purple-500">
                      Monthly Breakdown
                    </h3>
                    <div className="space-y-2">
                      {perDiemData.monthly_breakdown.map((month) => (
                        <div key={month.month} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                          <div>
                            <span className="text-sm font-medium">{month.month}</span>
                            <span className="text-xs text-muted-foreground ml-2">
                              ({month.partial_count}P + {month.full_count}F)
                            </span>
                          </div>
                          <span className="font-semibold text-purple-500">{formatCurrency(month.total)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
